import React, { useState } from 'react';
import { Modal, Skeleton, Spin } from 'antd';
import { ThemeTable } from '../components';
import dayjs from 'dayjs';
import { happy_dog, user } from '../../assets/images/images';
import { EyeOutlined } from '@ant-design/icons';
import UpdateInvoiceModal from '../updateInvoiceModal/updateInvoiceModal';
import {
  useUpdateAppointmentMutation,
  useLazyGetAppointmentByIdQuery,
} from '../../services/appointments';
import { sortDogsByName } from '../../helpers/helpers';

const DogsModal = ({
  isOpen,
  onClose,
  data,
  isLoading,
  onViewUserDetails,
  onViewDog,
  onRefresh,
  zIndex,
}) => {
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [isFetchingAppointment, setIsFetchingAppointment] = useState(false);
  const [updateAppointment, { isLoading: isUpdating }] =
    useUpdateAppointmentMutation();
  const [getAppointmentById] = useLazyGetAppointmentByIdQuery();

  const handleViewAppointment = async appointment => {
    setIsFetchingAppointment(true);
    try {
      const response = await getAppointmentById(appointment._id);
      if (response?.data?.data) {
        setSelectedAppointment(response.data.data);
      } else {
        // Fallback to the partial data if fetch fails
        setSelectedAppointment(appointment);
      }
    } catch (error) {
      console.error('Error fetching appointment:', error);
      setSelectedAppointment(appointment);
    }
    setIsFetchingAppointment(false);
    setIsInvoiceModalOpen(true);
  };

  const handleCloseInvoiceModal = () => {
    setIsInvoiceModalOpen(false);
    setSelectedAppointment(null);
  };

  const handleUpdateInvoice = async inv => {
    const response = await updateAppointment({
      id: selectedAppointment?._id,
      body: { appointment: { ...inv } },
    });
    if (!response?.data?.error) {
      onRefresh?.();
    }
  };
  const columns = [
    {
      title: 'Avatar',
      dataIndex: 'avatar',
      key: 'avatar',
      render: (image, record) => (
        <div key={`avatar-${record._id}`} className="flex justify-center">
          <img
            className="w-10 h-10 rounded-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
            src={image || happy_dog}
            alt="Dog avatar"
            onClick={() => onViewDog && onViewDog(record._id)}
          />
        </div>
      ),
    },
    {
      title: 'Dog Name',
      dataIndex: 'dog_name',
      key: 'dog_name',
      render: (text, record) => (
        <p
          key={`dog-name-${record._id}`}
          className="text-center font-medium cursor-pointer hover:text-primary transition-colors"
          onClick={() => onViewDog && onViewDog(record._id)}
        >
          {text}
        </p>
      ),
    },
    {
      title: 'Gender',
      dataIndex: 'gender',
      key: 'gender',
      render: (text, record) => (
        <p key={`gender-${record._id}`} className="text-center font-medium">
          {text}
        </p>
      ),
    },
    {
      title: 'Age',
      dataIndex: 'birthday',
      key: 'birthday',
      render: (text, record) => (
        <p key={`age-${record._id}`} className="text-center font-medium">
          {text && dayjs().diff(dayjs(text), 'year')}
        </p>
      ),
    },
    {
      title: 'Color',
      dataIndex: 'color',
      key: 'color',
      render: (text, record) => (
        <p key={`color-${record._id}`} className="font-medium text-center">
          {text}
        </p>
      ),
    },
  ];

  const appointmentColumns = [
    {
      title: 'Start Date',
      dataIndex: 'appointment_start_time',
      key: 'appointment_start_time',
      render: (text, record) => (
        <p
          key={`start-time-${record._id}`}
          className="text-center font-medium text-primary cursor-pointer hover:underline"
          onClick={() => handleViewAppointment(record)}
        >
          {text ? dayjs(text).format('MMM D, YYYY') : '-'}
        </p>
      ),
    },
    {
      title: 'End Date',
      dataIndex: 'appointment_end_time',
      key: 'appointment_end_time',
      render: (text, record) => (
        <p key={`end-time-${record._id}`} className="text-center font-medium">
          {text ? dayjs(text).format('MMM D, YYYY') : '-'}
        </p>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text, record) => (
        <p
          key={`status-${record._id}`}
          className="text-center font-medium capitalize"
        >
          {text?.replace('_', ' ') || '-'}
        </p>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'netTotal',
      key: 'netTotal',
      render: (text, record) => (
        <p key={`price-${record._id}`} className="text-center font-medium">
          ${(text || record.priceAtAppoTime || 0).toFixed(2)}
        </p>
      ),
    },
    {
      title: 'Paid',
      dataIndex: 'isPaid',
      key: 'isPaid',
      render: (text, record) => (
        <p
          key={`paid-${record._id}`}
          className={`font-medium text-center ${text ? 'text-green-600' : 'text-red-600'}`}
        >
          {text ? 'Yes' : 'No'}
        </p>
      ),
    },
    {
      title: '',
      dataIndex: '_id',
      key: 'action',
      width: 50,
      render: (_, record) =>
        isFetchingAppointment ? (
          <Spin size="small" key={`spin-${record._id}`} />
        ) : (
          <EyeOutlined
            key={`view-${record._id}`}
            className="text-primary cursor-pointer text-lg hover:opacity-70"
            onClick={() => handleViewAppointment(record)}
          />
        ),
    },
  ];

  return (
    <Modal
      //   className="-mt-14"
      width={800}
      footer={false}
      open={isOpen}
      onCancel={onClose}
      zIndex={zIndex}
    >
      <div className="my-8">
        {isLoading ? (
          <Skeleton />
        ) : (
          <>
            <div className="flex my-[10px] items-center justify-between">
              <div className="flex items-center">
                <img
                  src={data?.user?.avatar || user}
                  className="w-16 h-16 rounded-full object-cover"
                  alt="avatar"
                />
                <div className="flex flex-col ml-2">
                  <p
                    className="text-xl font-semibold text-themeBlack cursor-pointer hover:text-primary transition-colors"
                    onClick={() => onViewUserDetails && onViewUserDetails(data)}
                  >
                    {data?.user?.full_name}
                  </p>
                  <p
                    className="lg text-themeGray-dark cursor-pointer hover:text-primary transition-colors"
                    onClick={() => onViewUserDetails && onViewUserDetails(data)}
                  >
                    {data?.user?.email}
                  </p>
                </div>
              </div>
              {data?.totalUnpaidPrice > 0 ? (
                <div className="flex flex-col items-end bg-red-50 border border-red-200 rounded-lg px-4 py-2">
                  <p className="text-sm text-red-600 font-medium">
                    Total Unpaid Amount
                  </p>
                  <p className="text-2xl font-bold text-red-600">
                    ${(data?.totalUnpaidPrice || 0).toFixed(2)}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-end bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                  <p className="text-sm text-green-600 font-medium">Balance</p>
                  <p className="text-2xl font-bold text-green-600">$0.00</p>
                </div>
              )}
            </div>
            <ThemeTable
              columns={columns}
              data={sortDogsByName(data?.dogs)}
              entityName="dogs"
              emptyText="This owner hasn't added any dogs yet."
            />
            {data?.acceptedAppointments?.length > 0 && (
              <>
                <p className="text-lg font-semibold mt-4 mb-2">
                  Unpaid Appointments ({data.acceptedAppointments.length})
                </p>
                <ThemeTable
                  columns={appointmentColumns}
                  data={data?.acceptedAppointments}
                />
              </>
            )}
          </>
        )}
      </div>

      <UpdateInvoiceModal
        isOpen={isInvoiceModalOpen}
        onClose={handleCloseInvoiceModal}
        data={selectedAppointment}
        setData={setSelectedAppointment}
        onSubmit={handleUpdateInvoice}
        isLoading={isUpdating}
        onInvoiceSent={onRefresh}
        zIndex={(zIndex || 1000) + 10}
      />
    </Modal>
  );
};

export default DogsModal;
